import type { DiscoverItem, Trip, TripDay } from "../../types";

/**
 * What a trip costs and what it says when shared.
 *
 * A port of the phone's `apps/mobile/src/features/discover/lib/trip-metrics.ts`,
 * kept behaviourally identical so the same trip quotes the same total on both.
 * It is a copy rather than an import because the two apps resolve a stop to a
 * different item type and format money through different helpers; the rules
 * below (bookable stops only, currency from the first priced stop, MAD
 * default) are the part that must not drift.
 */

export type ResolveItem = (itemId: string) => DiscoverItem | null;

/**
 * Pull the numeric amount out of a bookable priceLabel ("MAD 450", "EUR 42").
 * Returns null for an explore row's editorial range or anything that does not
 * parse cleanly.
 */
export function parsePriceAmount(priceLabel: string | null): number | null {
  if (!priceLabel) return null;
  const m = priceLabel.replace(/[\s,]/g, "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Price that will actually be due on the day. Bookable stops only. */
export function stopDue(item: DiscoverItem | null): number | null {
  if (!item || item.mode !== "bookable") return null;
  return parsePriceAmount(item.priceLabel);
}

export function dayDue(day: TripDay, resolveItem: ResolveItem): number {
  return day.stops.reduce((sum, s) => sum + (stopDue(resolveItem(s.itemId)) ?? 0), 0);
}

export function tripDue(trip: Trip, resolveItem: ResolveItem): number {
  return trip.days.reduce((sum, d) => sum + dayDue(d, resolveItem), 0);
}

export function tripStopCount(trip: Trip): number {
  return trip.days.reduce((n, d) => n + d.stops.length, 0);
}

/** The currency prefix of the first bookable stop ("MAD", "EUR"), MAD default. */
export function tripCurrency(trip: Trip, resolveItem: ResolveItem): string {
  for (const d of trip.days) {
    for (const s of d.stops) {
      const item = resolveItem(s.itemId);
      if (item?.mode === "bookable" && item.priceLabel) {
        const m = item.priceLabel.trim().match(/^([A-Z]{3})\b/);
        if (m) return m[1]!;
      }
    }
  }
  return "MAD";
}

/**
 * Plain-text itinerary for the share sheet.
 *
 * Never a payment link and never a total framed as charged: an activity is
 * paid on arrival, so the last two lines say what is due on the day.
 */
export function buildShareText(
  trip: Trip,
  resolveItem: ResolveItem,
  labels: { due: string; payNote: string },
  formatMoney: (amount: number, currency: string) => string,
  /**
   * The total the page shows, when the caller has priced the days for the
   * party. Left out, the old per-stop sum is used (the phone's port).
   */
  totalOverride?: number,
): string {
  const lines: string[] = [trip.name];
  for (const day of trip.days) {
    lines.push("", day.label);
    if (day.stops.length === 0) {
      lines.push("  -");
      continue;
    }
    for (const s of day.stops) {
      const item = resolveItem(s.itemId);
      const time = s.startTime ? `${s.startTime} ` : "";
      const due = stopDue(item);
      const price = due != null && item?.priceLabel ? ` (${item.priceLabel})` : "";
      lines.push(`  • ${time}${item?.title ?? s.itemId}${price}`);
    }
  }
  const total = totalOverride ?? tripDue(trip, resolveItem);
  if (total > 0) {
    lines.push("", `${labels.due}: ${formatMoney(total, tripCurrency(trip, resolveItem))}`);
    lines.push(labels.payNote);
  }
  return lines.join("\n");
}
